import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Cloud, FileText, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLang } from '../utils/i18n';
import { parseImport } from '../utils/weights';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface ImportModalProps {
  onClose: () => void;
  /** Weights come from a `name,weight` CSV; a plain list yields weight 1. */
  onImport: (entries: { name: string; weight: number }[]) => void;
}

type Mode = 'local' | 'cloud';
type Status = 'idle' | 'importing' | 'success' | 'error';

const ImportModal: React.FC<ImportModalProps> = ({ onClose, onImport }) => {
  const { t } = useLang();
  const [mode, setMode] = useState<Mode>('local');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [fileName, setFileName] = useState('');
  const [url, setUrl] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const modalRef = useFocusTrap(true);

  /**
   * The success tick is held for 600 ms before the import is applied. Closing
   * the modal inside that window used to leave the timer running, so the entries
   * arrived anyway — a cancelled import that still imported. Cancel on unmount.
   */
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Recognises the two-column `name,weight` CSV that Export writes, and falls
  // back to the long-standing "split on any delimiter" behaviour otherwise.
  const parseText = parseImport;

  const handleFile = (file: File) => {
    setFileName(file.name);
    setStatus('importing');
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const names = parseText(String(reader.result ?? ''));
        if (names.length === 0) throw new Error('empty');
        setStatus('success');
        commitTimer.current = setTimeout(() => { onImport(names); onClose(); }, 600);
      } catch {
        setStatus('error');
        setErrorMsg(t('import.parseError'));
      }
    };
    reader.onerror = () => { setStatus('error'); setErrorMsg(t('import.parseError')); };
    reader.readAsText(file);
  };

  const handleFetch = async () => {
    if (!url.trim()) return;
    setStatus('importing');
    try {
      const res = await fetch(url.trim());
      if (!res.ok) throw new Error('fetch failed');
      const text = await res.text();
      const names = parseText(text);
      if (names.length === 0) throw new Error('empty');
      setStatus('success');
      commitTimer.current = setTimeout(() => { onImport(names); onClose(); }, 600);
    } catch {
      setStatus('error');
      setErrorMsg(t('import.fetchError'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-modal-title"
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 id="import-modal-title" className="text-lg font-bold text-gray-900 dark:text-white">{t('import.title')}</h2>
          <button onClick={onClose} aria-label={t('import.close')} className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 p-4 pb-2">
          <button
            onClick={() => { setMode('local'); setStatus('idle'); }}
            className={`flex-1 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              mode === 'local' ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 ring-2 ring-violet-400' : 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Upload size={16} /> {t('import.local')}
          </button>
          <button
            onClick={() => { setMode('cloud'); setStatus('idle'); }}
            className={`flex-1 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              mode === 'cloud' ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 ring-2 ring-violet-400' : 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Cloud size={16} /> {t('import.cloud')}
          </button>
        </div>

        {/* Content */}
        <div className="px-5 pb-5">
          {mode === 'local' ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('import.localDesc')}</p>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl p-6 text-center cursor-pointer hover:border-violet-400 hover:bg-violet-50/30 dark:hover:bg-violet-900/10 transition-all"
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".txt,.csv,text/plain,text/csv"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                />
                <FileText size={28} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{t('import.chooseFile')}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{fileName || t('import.noFile')}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('import.fileTypes')}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('import.cloudDesc')}</p>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={url}
                  onChange={e => { setUrl(e.target.value); setStatus('idle'); }}
                  placeholder={t('import.urlPlaceholder')}
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent placeholder-gray-300 dark:placeholder-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all"
                />
                <button
                  onClick={handleFetch}
                  disabled={!url.trim() || status === 'importing'}
                  className="px-4 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {status === 'importing' ? <Loader2 size={14} className="animate-spin" /> : <Cloud size={14} />}
                  {t('import.fetch')}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('import.cloudTip')}
              </p>
            </div>
          )}

          {/* Status */}
          {status === 'importing' && (
            <div className="mt-4 flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400">
              <Loader2 size={16} className="animate-spin" /> {t('import.importing')}
            </div>
          )}
          {status === 'success' && (
            <div className="mt-4 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircle2 size={16} /> {t('import.imported', { n: '' })}
            </div>
          )}
          {status === 'error' && (
            <div className="mt-4 flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" /> {errorMsg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;


